import { HttpErrorSkipConfig } from './http-error-skip.config';
import { HttpErrorResponse } from '@angular/common/http';
import { isNumberArray } from '@shared/utils/arrays';

/**
 * Creates the configuration needed to skip the HttpErrorInterceptor.
 * It creates the necessary values to add to the request header,
 * that is needed to configure the behaviour of the interceptor.
 *
 * If status code ALL_STATUS_CODE (0) is specified, it will skip all status codes.
 *
 * ## Example
 * This request will skip the HttpErrorInterceptor when the status code of the request is 404.
 * ```ts
 * this.http
 *  .get(url, {
 *    headers: {
 *      [HttpErrorSkip.HEADER_KEY]: new HttpErrorSkip({
 *        skipStatusCodes: [404],
 *      }).parseToString(),
 *    },
 *  })
 * ```
 * @see HttpErrorInterceptor
 */
export class HttpErrorSkip {
  private readonly skipStatusCodes: number[];
  private readonly interceptOnlyStatusCodes: number[];

  public static HEADER_KEY = 'skip-interceptor';
  public static ALL_STATUSES_CODE = 0;

  constructor(config?: HttpErrorSkipConfig) {
    if (isNumberArray(config?.skipStatusCodes)) {
      this.skipStatusCodes = config.skipStatusCodes;
    } else if (isNumberArray(config?.interceptOnlyStatusCodes)) {
      this.interceptOnlyStatusCodes = config.interceptOnlyStatusCodes;
    }
  }

  static fromString(config: string): HttpErrorSkip {
    return new HttpErrorSkip(JSON.parse(config));
  }

  parseToString(): string {
    return JSON.stringify({
      skipStatusCodes: this.skipStatusCodes,
      interceptOnlyStatusCodes: this.interceptOnlyStatusCodes,
    });
  }

  shouldSkip(err: HttpErrorResponse): boolean {
    if (!(err instanceof HttpErrorResponse)) return false;

    if (this.skipStatusCodes) {
      return (
        this.skipStatusCodes.includes(err.status) ||
        this.skipStatusCodes.includes(HttpErrorSkip.ALL_STATUSES_CODE)
      );
    }

    if (this.interceptOnlyStatusCodes) {
      return !this.interceptOnlyStatusCodes.includes(err.status);
    }

    return true;
  }
}
